import {
    SCENE_CONFIGURATOR_LEGACY_SHARE_PARAM,
    SCENE_CONFIGURATOR_SHARE_PARAM,
    shouldPromptForSharedAR
} from './share-config-utils.js';
import { isPhoneLikeMobileDevice } from './scene-overlay-utils.js';
import {
    applyProductOptionsCadConfigToSceneLoader,
    buildProductOptionsInitOptions,
    primeSceneConfiguratorAssemblyState,
    sanitizeCadOptions
} from './scene-product-options-handoff.js';

let sceneModulesPromise = null;

function getVersionedDistModuleUrl(fileName) {
    const runtimeConfig = window.sceneConfiguratorNewScene || window.sceneConfiguratorConfig || {};
    const pluginUrl = runtimeConfig.pluginUrl || window.sceneConfiguratorConfig?.pluginUrl || '/wp-content/plugins/scene-configurator/';
    const assetVersion = runtimeConfig.assetVersion || window.sceneConfiguratorConfig?.assetVersion || window.sceneConfiguratorConfig?.version || '';
    const baseUrl = `${pluginUrl.replace(/\/?$/, '/')}assets/dist/${fileName}`;

    if (!assetVersion) {
        return baseUrl;
    }

    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}ver=${encodeURIComponent(assetVersion)}`;
}

function isSceneBootstrapDebugEnabled() {
    return window.sceneConfiguratorConfig?.enableDebugConsole === true
        || window.sceneConfiguratorConfig?.sceneBootstrapDebug === true;
}

function sceneBootstrapDebugLog(...args) {
    if (isSceneBootstrapDebugEnabled()) {
        console.log(...args);
    }
}

function loadSceneModules() {
    if (!sceneModulesPromise) {
        const sceneLoaderModulePromise = import(/* @vite-ignore */ getVersionedDistModuleUrl('SceneLoader.js'));
        const devOverlayModulePromise = import(/* @vite-ignore */ getVersionedDistModuleUrl('RampDevOverlay.js'))
            .catch((error) => {
                console.warn('[newScene] Ramp development overlay failed to load:', error);
                return { mountRampDevOverlay: null };
            });

        sceneModulesPromise = Promise.all([
            sceneLoaderModulePromise,
            devOverlayModulePromise
        ]).then(([sceneLoaderModule, devOverlayModule]) => ({
            SceneLoader: sceneLoaderModule.SceneLoader || sceneLoaderModule.S,
            mountRampDevOverlay: devOverlayModule.mountRampDevOverlay || null
        }));
    }

    return sceneModulesPromise;
}

function decodeSharePayload(value = '') {
    if (!value) {
        return null;
    }

    try {
        const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
        const binary = atob(padded);
        const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    } catch (error) {
        console.warn('[newScene] Failed to decode shared configuration:', error);
        return null;
    }
}

function getLegacySharedCadOptionsFromUrl() {
    const payload = decodeSharePayload(new URLSearchParams(window.location.search).get(SCENE_CONFIGURATOR_LEGACY_SHARE_PARAM) || '');
    if (!payload || typeof payload !== 'object') {
        return {};
    }

    return sanitizeCadOptions(payload.cad_options || payload);
}

async function getSharedCadOptionsFromToken() {
    const token = new URLSearchParams(window.location.search).get(SCENE_CONFIGURATOR_SHARE_PARAM) || '';
    if (!token) {
        return {};
    }

    const runtimeConfig = typeof sceneConfiguratorConfig !== 'undefined'
        ? sceneConfiguratorConfig
        : {};
    if (!runtimeConfig.ajaxUrl) {
        console.warn('[newScene] Share lookup is not configured on this page.');
        return {};
    }

    try {
        const shareLookupUrl = new URL(runtimeConfig.ajaxUrl, window.location.origin);
        shareLookupUrl.searchParams.set('action', 'scene_configurator_get_ar_share');
        shareLookupUrl.searchParams.set('token', token);

        const response = await fetch(shareLookupUrl.toString(), {
            method: 'GET',
            credentials: 'same-origin',
        });
        const payload = await response.json();
        if (!response.ok || !payload?.success) {
            console.warn('[newScene] Shared configuration lookup failed:', payload?.data?.message || response.status);
            return {};
        }

        return sanitizeCadOptions(payload?.data?.cad_options || {});
    } catch (error) {
        console.warn('[newScene] Shared configuration lookup failed:', error);
        return {};
    }
}

// Get canvas element
const canvas = document.getElementById("canvas");

if (!canvas) {
    console.error("Canvas element with id 'canvas' not found");
} else {
    // Log version
    const version = typeof sceneConfiguratorConfig !== 'undefined' && sceneConfiguratorConfig.version 
        ? sceneConfiguratorConfig.version 
        : 'unknown';
    sceneBootstrapDebugLog(`Scene Configurator v${version} - newScene.js loaded`);
    
    // Get scene configuration from data attribute
    const sceneSlug = canvas.dataset.scene || 'scene-1';
    applyPresentationHostFrameSizing();
    bindPresentationSliderEdgeZones();
    
    // Fetch scene configuration from WordPress
    fetchSceneConfig(sceneSlug).then(config => {
        initializeScene(applyCanvasConfigOverrides(config));
    }).catch(error => {
        console.error("Failed to load scene configuration:", error);
        // Fallback to basic scene
        initializeScene(applyCanvasConfigOverrides(getDefaultConfig()));
    });
}

async function fetchSceneConfig(slug) {
    sceneBootstrapDebugLog('fetchSceneConfig called with slug:', slug);
    
    // Use localized REST root from WordPress (handles both pretty and ugly permalinks)
    const restRoot = typeof sceneConfiguratorConfig !== 'undefined' && sceneConfiguratorConfig.restRoot 
        ? sceneConfiguratorConfig.restRoot 
        : '/wp-json/';
    const apiUrl = `${restRoot}scene-configurator/v1/scenes/${slug}`;
    
    sceneBootstrapDebugLog('Fetching from API:', apiUrl);
    sceneBootstrapDebugLog('REST root:', restRoot);
    
    try {
        const response = await fetch(apiUrl);
        sceneBootstrapDebugLog('API response status:', response.status);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const config = await response.json();
        sceneBootstrapDebugLog('Fetched scene config from API:', config);
        sceneBootstrapDebugLog('Background module from API:', config.background_module);
        return config;
    } catch (error) {
        console.error('Failed to fetch scene config from API:', error);
        sceneBootstrapDebugLog('Falling back to default config');
        return getDefaultConfig();
    }
}

function getDefaultConfig() {
    return {
        name: 'Scene-1',
        slug: 'scene-1',
        background_module: 'sky',
        ground_module: 'grass',
        cad_module: 'Scene1-Replicad-Sample',
        camera_settings: {
            position: { x: 0, y: 5, z: 10 },
            fov: 75,
            near: 0.1,
            far: 1000
        },
        lighting_settings: {
            preset: 'custom',
            ambient: { color: 0xffffff, intensity: 0.6 },
            directional: {
                color: 0xffffff,
                intensity: 0.8,
                position: { x: 5, y: 10, z: 7.5 }
            },
            hemisphere: { enabled: false, skyColor: 0xdbeafe, groundColor: 0x6b7280, intensity: 0.35 },
            fill: { enabled: false, color: 0xffffff, intensity: 0.25, position: { x: -6, y: 4, z: -5 } },
            rim: { enabled: false, color: 0xffffff, intensity: 0.35, position: { x: -4, y: 6, z: -8 } },
            top: { enabled: false, color: 0xffffff, intensity: 0.2, position: { x: 0, y: 10, z: 0 } },
            shadows: { enabled: false, softness: 'soft', mapSize: 2048, cameraSize: 40, bias: -0.0005, normalBias: 0.02, cast: true, receive: true },
            shadowReceiver: { enabled: false, color: 0x000000, opacity: 0.3, size: 48, y: 0 },
            contactShadow: { enabled: false, color: 0x000000, opacity: 0.28, size: 22, blur: 0.62, y: 0.01 },
            blobShadow: { enabled: false, color: 0x000000, opacity: 0.18, size: 16, x: 0, z: 0, y: 0.012 },
            studioShadow: { enabled: false },
            renderer: { toneMapping: 'aces', exposure: 1 }
        },
        controls_settings: {
            enableDamping: true,
            dampingFactor: 0.05,
            target: { x: 0, y: 0, z: 0 }
        }
    };
}

function cloneJsonLike(value) {
    return value ? JSON.parse(JSON.stringify(value)) : value;
}

function getSceneBootstrapViewportSize() {
    const viewport = window.visualViewport;
    return {
        width: viewport?.width || window.innerWidth || 0,
        height: viewport?.height || window.innerHeight || 0
    };
}

function isSceneBootstrapTouchDevice() {
    return navigator.maxTouchPoints > 0
        || window.matchMedia?.('(pointer: coarse)')?.matches === true;
}

function shouldApplyProductOptionsMobileOverrideSkin() {
    const viewport = getSceneBootstrapViewportSize();
    return isPhoneLikeMobileDevice({
        isTouchDevice: isSceneBootstrapTouchDevice(),
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
        userAgent: navigator.userAgent || ''
    });
}

function resolveProductOptionsMobileOverrideSkin(skin = '') {
    return shouldApplyProductOptionsMobileOverrideSkin()
        ? String(skin || '').trim()
        : '';
}

function normalizeProductOptionsDisplayMode(mode, allowInherit = false) {
    const normalizedMode = String(mode || '').trim().toLowerCase();
    const allowedModes = ['full', 'cart_actions_only', 'hidden'];
    if (allowInherit) {
        allowedModes.push('inherit');
    }

    return allowedModes.includes(normalizedMode)
        ? normalizedMode
        : (allowInherit ? 'inherit' : 'full');
}

function isCanvasPresentationMode() {
    return canvas?.dataset?.scenePresentationMode === 'true';
}

function getSceneScopedProductOptions(sceneSlug = '') {
    const normalizedSlug = String(sceneSlug || '').trim();
    const scopedOptions = normalizedSlug
        ? window.sceneConfiguratorProductOptionsByScene?.[normalizedSlug]
        : null;
    if (Array.isArray(scopedOptions) && scopedOptions.length > 0) {
        return scopedOptions;
    }

    return Array.isArray(window.sceneConfiguratorProductOptions)
        ? window.sceneConfiguratorProductOptions
        : [];
}

function getSceneScopedProductMeta(sceneSlug = '') {
    const normalizedSlug = String(sceneSlug || '').trim();
    const scopedMeta = normalizedSlug
        ? window.sceneConfiguratorProductMetaByScene?.[normalizedSlug]
        : null;
    if (scopedMeta && typeof scopedMeta === 'object') {
        return scopedMeta;
    }

    return window.sceneConfiguratorProductMeta || {};
}

function shouldAcceptExternalConfiguratorOptionsState(detail = {}, productMeta = {}) {
    if (!detail || typeof detail !== 'object') {
        return false;
    }

    const eventProductId = Number(detail.productId || 0);
    const sceneProductId = Number(productMeta?.id || 0);
    if (eventProductId > 0 && sceneProductId > 0 && eventProductId !== sceneProductId) {
        return false;
    }

    return detail.config && typeof detail.config === 'object' && Object.keys(detail.config).length > 0;
}

function normalizeComparableCadOptionValue(value) {
    if (value && typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch (error) {
            return String(value);
        }
    }

    return String(value ?? '');
}

function getChangedCadOptionKeys(nextConfig = {}, currentCadOptions = {}) {
    const nextCadOptions = sanitizeCadOptions(nextConfig);

    return Object.keys(nextCadOptions).filter((key) => {
        const nextValue = normalizeComparableCadOptionValue(nextCadOptions[key]);
        const currentValue = normalizeComparableCadOptionValue(currentCadOptions?.[key]);
        return nextValue !== currentValue;
    });
}

function getInstantUpdateHandlerKeys(sceneLoader) {
    const handlers = sceneLoader?.cadModule?.instantUpdateHandlers;
    return handlers && typeof handlers === 'object'
        ? new Set(Object.keys(handlers).filter(Boolean))
        : new Set();
}

function resolveExternalConfiguratorOptionsRebuildMode(sceneLoader, nextConfig = {}) {
    const changedKeys = getChangedCadOptionKeys(nextConfig, sceneLoader?.config?.cad_options || {});
    if (changedKeys.length === 0) {
        return false;
    }

    const instantUpdateHandlerKeys = getInstantUpdateHandlerKeys(sceneLoader);
    if (instantUpdateHandlerKeys.size === 0) {
        return true;
    }

    return !changedKeys.every((key) => instantUpdateHandlerKeys.has(key));
}

function shouldUseExternalConfiguratorOptionsOnly(config = {}, { presentationMode = false } = {}) {
    return presentationMode === true
        && config?.product_options_enabled === true
        && config?.product_options_presentation_mode === true;
}

function bindExternalConfiguratorOptionsStateSync({
    productMeta = {},
    sceneLoader,
    rampDevOverlayRef,
} = {}) {
    if (!isCanvasPresentationMode() || !sceneLoader) {
        return;
    }

    document.addEventListener('configurator-options:state-change', (event) => {
        const detail = event.detail || {};
        if (!shouldAcceptExternalConfiguratorOptionsState(detail, productMeta)) {
            return;
        }

        sceneBootstrapDebugLog('[newScene] Applying external Configurator Options state to presentation scene:', detail.config);
        applyProductOptionsCadConfigToSceneLoader(sceneLoader, detail.config, resolveExternalConfiguratorOptionsRebuildMode(sceneLoader, detail.config), {
            rampDevOverlay: rampDevOverlayRef?.(),
        });
    });
}

function getLatestExternalConfiguratorOptionsState(productMeta = {}) {
    const store = window.ConfiguratorOptionsLatestStates || {};
    const productId = Number(productMeta?.id || 0);
    const candidates = [];

    if (productId > 0 && store.byProductId?.[productId]) {
        candidates.push(store.byProductId[productId]);
    }
    if (store.current) {
        candidates.push(store.current);
    }

    return candidates.find((candidate) => shouldAcceptExternalConfiguratorOptionsState(candidate, productMeta)) || null;
}

function applyLatestExternalConfiguratorOptionsState({
    productMeta = {},
    sceneLoader,
    rampDevOverlayRef,
} = {}) {
    const latestState = getLatestExternalConfiguratorOptionsState(productMeta);
    if (!latestState || !sceneLoader) {
        return;
    }

    sceneBootstrapDebugLog('[newScene] Applying cached Configurator Options state to presentation scene:', latestState.config);
    applyProductOptionsCadConfigToSceneLoader(sceneLoader, latestState.config, resolveExternalConfiguratorOptionsRebuildMode(sceneLoader, latestState.config), {
        rampDevOverlay: rampDevOverlayRef?.(),
    });
}

function applyPresentationHostFrameSizing() {
    if (!isCanvasPresentationMode() || !canvas) {
        return;
    }

    const setImportant = (element, property, value) => {
        element?.style?.setProperty?.(property, value, 'important');
    };

    let element = canvas.closest('#scene-configurator-scene, .scene-configurator-3d-scene');
    let depth = 0;
    while (element && depth < 14) {
        setImportant(element, 'width', '100%');
        setImportant(element, 'max-width', 'none');
        setImportant(element, 'height', '100%');
        setImportant(element, 'min-height', '0');
        setImportant(element, 'box-sizing', 'border-box');

        if (element.classList?.contains('n2-ss-slide')) {
            break;
        }

        element = element.parentElement;
        depth += 1;
    }
}

function getPresentationPointerEscapeEdgePx() {
    const rawValue = Number(canvas?.dataset?.scenePointerEscapeEdgePx ?? 80);
    if (!Number.isFinite(rawValue)) {
        return 80;
    }

    return Math.max(0, Math.min(200, rawValue));
}

function getPresentationSliderRoot(host) {
    let sliderRoot = host.closest?.('.n2-ss-slider') || null;
    let candidate = host;
    while (!sliderRoot && candidate) {
        if (/^n2-ss-\d+$/.test(candidate.id || '')) {
            sliderRoot = candidate;
            break;
        }
        candidate = candidate.parentElement;
    }

    return sliderRoot || document.querySelector?.('.n2-ss-slider, [id^="n2-ss-"]') || null;
}

function triggerPresentationSliderNavigation(direction) {
    if (!canvas) {
        return false;
    }

    const host = canvas.closest?.('#scene-configurator-scene, .scene-configurator-3d-scene') || canvas;
    const sliderRoot = getPresentationSliderRoot(host);
    if (!sliderRoot) {
        return false;
    }

    const selectors = direction === 'previous'
        ? ['.nextend-arrow-previous', '.n2-ss-widget-previous', '[data-action="previous"]']
        : ['.nextend-arrow-next', '.n2-ss-widget-next', '[data-action="next"]'];
    const arrow = selectors
        .map((selector) => sliderRoot.querySelector?.(selector))
        .find(Boolean);

    if (!arrow) {
        return false;
    }

    if (typeof arrow.click === 'function') {
        arrow.click();
    } else {
        arrow.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    }
    return true;
}

function getPresentationSmartSliderTouchControl(host) {
    const sliderRoot = getPresentationSliderRoot(host);
    const touchControl = sliderRoot?.ss?.controls?.touch || null;
    if (!touchControl || typeof touchControl.initTouch !== 'function') {
        return null;
    }

    return touchControl;
}

function isPresentationSmartSliderSlideActive(host) {
    const slide = host?.closest?.('.n2-ss-slide');
    return !slide || slide.classList.contains('n2-ss-slide-active');
}

function pausePresentationSmartSliderDrag(host) {
    const touchControl = getPresentationSmartSliderTouchControl(host);
    if (
        !touchControl
        || touchControl.__sceneConfiguratorPresentationDragPaused === true
        || typeof touchControl.eventBurrito?.kill !== 'function'
    ) {
        return false;
    }

    touchControl.eventBurrito.kill();
    touchControl.__sceneConfiguratorPresentationDragPaused = true;
    return true;
}

function restorePresentationSmartSliderDrag(host) {
    const touchControl = getPresentationSmartSliderTouchControl(host);
    if (!touchControl || touchControl.__sceneConfiguratorPresentationDragPaused !== true) {
        return false;
    }

    touchControl.initTouch();
    touchControl.__sceneConfiguratorPresentationDragPaused = false;
    return true;
}

function bindPresentationSmartSliderDragGate(host) {
    if (!isCanvasPresentationMode() || !canvas || !host || canvas.dataset.scenePresentationSmartSliderDragGateBound === 'true') {
        return;
    }

    const sliderRoot = getPresentationSliderRoot(host);
    const slide = host.closest?.('.n2-ss-slide') || null;
    if (!sliderRoot || !slide) {
        return;
    }

    canvas.dataset.scenePresentationSmartSliderDragGateBound = 'true';
    canvas.style.setProperty('touch-action', 'none');
    const refreshSmartSliderDrag = () => {
        if (isPresentationSmartSliderSlideActive(host)) {
            pausePresentationSmartSliderDrag(host);
        } else {
            restorePresentationSmartSliderDrag(host);
        }
    };
    const scheduleRefresh = () => {
        window.setTimeout(refreshSmartSliderDrag, 0);
    };

    sliderRoot.addEventListener('sliderChangeCurrentSlide', scheduleRefresh);
    sliderRoot.addEventListener('mainAnimationComplete', scheduleRefresh);

    if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(scheduleRefresh);
        observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
    }

    scheduleRefresh();
    window.setTimeout(refreshSmartSliderDrag, 300);
}

function bindPresentationSliderEdgeZones() {
    if (!isCanvasPresentationMode() || !canvas || canvas.dataset.scenePresentationEdgeZonesBound === 'true') {
        return;
    }

    const edgePx = getPresentationPointerEscapeEdgePx();

    const host = canvas.closest?.('#scene-configurator-scene, .scene-configurator-3d-scene') || canvas.parentElement;
    if (!host) {
        return;
    }

    canvas.dataset.scenePresentationEdgeZonesBound = 'true';
    const computedPosition = window.getComputedStyle?.(host)?.position;
    if (!computedPosition || computedPosition === 'static') {
        host.style.setProperty('position', 'relative', 'important');
    }
    bindPresentationSmartSliderDragGate(host);
    if (edgePx <= 0) {
        return;
    }

    const makeEdgeZone = (direction) => {
        const side = direction === 'previous' ? 'left' : 'right';
        const existingZone = host.querySelector?.(`.scene-configurator-presentation-edge-zone--${side}`);
        const zone = existingZone || document.createElement('div');
        zone.className = `scene-configurator-presentation-edge-zone scene-configurator-presentation-edge-zone--${side}`;
        zone.setAttribute('aria-hidden', 'true');
        zone.dataset.scenePresentationEdgeDirection = direction;
        zone.style.setProperty('position', 'absolute');
        zone.style.setProperty('top', '0');
        zone.style.setProperty('bottom', '0');
        zone.style.setProperty(side, '0');
        zone.style.setProperty('width', `${edgePx}px`);
        zone.style.setProperty('z-index', '20');
        zone.style.setProperty('pointer-events', 'auto');
        zone.style.setProperty('background', 'transparent');
        zone.style.setProperty('touch-action', 'manipulation');

        let lastNavigationAt = 0;
        const navigateOnce = () => {
            const now = Date.now();
            if (now - lastNavigationAt < 250) {
                return;
            }
            lastNavigationAt = now;
            triggerPresentationSliderNavigation(direction);
        };
        const handleEvent = (event) => {
            event.stopPropagation();
            if (event.cancelable) {
                event.preventDefault();
            }

            if (event.type === 'pointerup' || event.type === 'click') {
                navigateOnce();
            }
        };

        [
            'pointerdown',
            'pointermove',
            'pointerup',
            'click',
            'touchstart',
            'touchmove',
            'touchend',
        ].forEach((eventName) => {
            zone.addEventListener(eventName, handleEvent, { passive: false });
        });

        if (!existingZone) {
            host.appendChild(zone);
        }
    };

    makeEdgeZone('previous');
    makeEdgeZone('next');
}

function normalizeSceneModuleOverride(value) {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
        return null;
    }

    return ['none', 'off', 'false', '0'].includes(normalizedValue.toLowerCase())
        ? ''
        : normalizedValue;
}

function parseCanvasSceneOptionsOverride(value) {
    const json = String(value || '').trim();
    if (!json) {
        return {};
    }

    try {
        const parsed = JSON.parse(json);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed
            : {};
    } catch (error) {
        console.warn('[newScene] Ignoring invalid scene options override JSON:', error);
        return {};
    }
}

function applyCanvasPresentationOverrides(config = {}) {
    if (!isCanvasPresentationMode()) {
        return config;
    }

    return {
        ...config,
        product_options_presentation_mode: true,
        product_options_enabled: true,
        product_options_display_mode: 'hidden',
        product_options_render_pane: false,
        user_controls: {
            ...(config.user_controls || {}),
            showFullscreen: false,
            showSceneTitle: false,
            showHelp: false,
            showSound: false,
            showAR: false,
            showResetView: false,
            showAssemblyDimensionsToggle: false,
            showModuleDimensionsToggle: false,
            showCameraAssist: false,
            cameraAssistStyle: 'none',
            cameraAssistStyleDesktop: 'none',
            cameraAssistStyleMobile: 'none',
            mobileOverrideEnabled: true,
            mobileShowFullscreen: false,
            mobileShowSceneTitle: false,
            mobileShowHelp: false,
            mobileShowSound: false,
            mobileShowAR: false,
        },
    };
}

function applyCanvasConfigOverrides(config = {}) {
    const nextConfig = { ...config };

    if (canvas?.dataset?.productOptionsMode === 'current_product') {
        nextConfig.product_options_enabled = true;
        nextConfig.product_options_mobile_override_skin = canvas?.dataset?.productOptionsMobileOverrideSkin || config.product_options_mobile_override_skin || '';
    }

    const backgroundModuleOverride = normalizeSceneModuleOverride(canvas?.dataset?.sceneBackgroundModule);
    if (backgroundModuleOverride !== null) {
        nextConfig.background_module = backgroundModuleOverride;
    }
    const groundModuleOverride = normalizeSceneModuleOverride(canvas?.dataset?.sceneGroundModule);
    if (groundModuleOverride !== null) {
        nextConfig.ground_module = groundModuleOverride;
    }

    const backgroundOptionsOverride = parseCanvasSceneOptionsOverride(canvas?.dataset?.sceneBackgroundOptions);
    if (Object.keys(backgroundOptionsOverride).length > 0) {
        nextConfig.background_options = {
            ...(nextConfig.background_options || {}),
            ...backgroundOptionsOverride,
        };
    }
    const groundOptionsOverride = parseCanvasSceneOptionsOverride(canvas?.dataset?.sceneGroundOptions);
    if (Object.keys(groundOptionsOverride).length > 0) {
        nextConfig.ground_options = {
            ...(nextConfig.ground_options || {}),
            ...groundOptionsOverride,
        };
    }

    const placementConfig = window.sceneConfiguratorProductMeta?.productOptionsPlacementConfig || {};
    if (placementConfig && typeof placementConfig === 'object') {
        const mobileOverride = placementConfig.mobileOverride && typeof placementConfig.mobileOverride === 'object'
            ? placementConfig.mobileOverride
            : null;
        const activePlacementConfig = mobileOverride?.enabled === true && shouldApplyProductOptionsMobileOverrideSkin()
            ? { ...placementConfig, ...mobileOverride }
            : placementConfig;
        if (placementConfig.skin) {
            nextConfig.product_options_skin = placementConfig.skin;
        }
        if (Object.prototype.hasOwnProperty.call(placementConfig, 'title')) {
            nextConfig.product_options_title = String(placementConfig.title || '');
        }
        const desktopDisplayMode = normalizeProductOptionsDisplayMode(placementConfig.displayMode || nextConfig.product_options_display_mode || 'full');
        const mobileDisplayMode = mobileOverride?.enabled === true && shouldApplyProductOptionsMobileOverrideSkin()
            ? normalizeProductOptionsDisplayMode(mobileOverride.displayMode, true)
            : 'inherit';
        nextConfig.product_options_display_mode = mobileDisplayMode === 'inherit' ? desktopDisplayMode : mobileDisplayMode;
        if (activePlacementConfig.collapsible !== null && activePlacementConfig.collapsible !== undefined) {
            nextConfig.product_options_collapsible = !!activePlacementConfig.collapsible;
        }
        if (activePlacementConfig.defaultState === 'expanded' || activePlacementConfig.defaultState === 'collapsed') {
            nextConfig.product_options_default_state = activePlacementConfig.defaultState;
        }
        if (activePlacementConfig.showPricing !== null && activePlacementConfig.showPricing !== undefined) {
            nextConfig.product_options_show_pricing = !!activePlacementConfig.showPricing;
        }
        if (activePlacementConfig.showPricingTaxLabel !== null && activePlacementConfig.showPricingTaxLabel !== undefined) {
            nextConfig.product_options_show_pricing_tax_label = !!activePlacementConfig.showPricingTaxLabel;
        }
        if (activePlacementConfig.includeResetInPricingSummary !== null && activePlacementConfig.includeResetInPricingSummary !== undefined) {
            nextConfig.product_options_include_reset_in_pricing_summary = !!activePlacementConfig.includeResetInPricingSummary;
        }
        if (activePlacementConfig.stickyPricing !== null && activePlacementConfig.stickyPricing !== undefined) {
            nextConfig.product_options_sticky_pricing = !!activePlacementConfig.stickyPricing;
        }
        if (activePlacementConfig.showPricingCollapsed !== null && activePlacementConfig.showPricingCollapsed !== undefined) {
            nextConfig.product_options_show_pricing_collapsed = !!activePlacementConfig.showPricingCollapsed;
        }
        if (mobileOverride?.enabled === true && shouldApplyProductOptionsMobileOverrideSkin()) {
            const productMeta = window.sceneConfiguratorProductMeta || {};
            if (activePlacementConfig.cartActionsEnabled !== null && activePlacementConfig.cartActionsEnabled !== undefined) {
                productMeta.cartActionsEnabled = !!activePlacementConfig.cartActionsEnabled;
            }
            if (activePlacementConfig.showViewCartButton !== null && activePlacementConfig.showViewCartButton !== undefined) {
                productMeta.showViewCartButton = !!activePlacementConfig.showViewCartButton;
            }
            window.sceneConfiguratorProductMeta = productMeta;
        }

        nextConfig.scene_layout_settings = {
            ...(nextConfig.scene_layout_settings || {})
        };
        if (activePlacementConfig.panePlacement === 'left' || activePlacementConfig.panePlacement === 'right') {
            nextConfig.scene_layout_settings.productOptionsPanePlacement = activePlacementConfig.panePlacement;
        }
        if (Number(activePlacementConfig.paneWidth) > 0) {
            nextConfig.scene_layout_settings.productOptionsPaneWidth = Number(activePlacementConfig.paneWidth);
        }
        if (activePlacementConfig.paneResizable !== null && activePlacementConfig.paneResizable !== undefined) {
            nextConfig.scene_layout_settings.productOptionsPaneResizable = !!activePlacementConfig.paneResizable;
        }
        if (Number(activePlacementConfig.paneTop) >= 0) {
            nextConfig.scene_layout_settings.productOptionsPaneTop = Number(activePlacementConfig.paneTop);
        }
        if (Number(activePlacementConfig.paneSideOffset) >= 0) {
            nextConfig.scene_layout_settings.productOptionsPaneSideOffset = Number(activePlacementConfig.paneSideOffset);
        }
        if (activePlacementConfig.cartEditingEnabled !== null && activePlacementConfig.cartEditingEnabled !== undefined) {
            nextConfig.scene_layout_settings.cartEditingEnabled = !!activePlacementConfig.cartEditingEnabled;
        }
    }

    return applyCanvasPresentationOverrides(nextConfig);
}

function applyProductContext(config) {
    const productMeta = window.sceneConfiguratorProductMeta || {};
    const sceneSlug = config?.slug || document.getElementById('canvas')?.dataset?.scene || '';

    config.product_context = {
        productId: productMeta?.id || null,
        sceneSlug,
        isPurchasableContext: productMeta?.isPurchasableContext === true,
        editingCartItemKey: productMeta?.editingCartItemKey || '',
        editingCartItemCadOptions: sanitizeCadOptions(productMeta?.editingCartItemCadOptions || {}),
    };

    config.scene_default_camera_settings = cloneJsonLike(config.camera_settings);
    config.scene_default_controls_settings = cloneJsonLike(config.controls_settings);

    return config;
}

async function initializeScene(config) {
    const { SceneLoader, mountRampDevOverlay } = await loadSceneModules();
    let sceneLoader = null;
    let rampDevOverlay = null;
    let productOptions = null;
    let latestProductOptionsConfig = {};
    const shouldShowSharedARPrompt = shouldPromptForSharedAR(window.location.href);
    const enableRamdevControls = typeof sceneConfiguratorConfig !== 'undefined'
        && (sceneConfiguratorConfig.isAdmin === true || sceneConfiguratorConfig.isAdmin === '1');

    const sceneSlug = config?.slug || canvas?.dataset?.scene || '';
    const productMeta = getSceneScopedProductMeta(sceneSlug);
    const productOptionsData = getSceneScopedProductOptions(sceneSlug);
    const useExternalProductOptionsOnly = shouldUseExternalConfiguratorOptionsOnly(config, {
        presentationMode: isCanvasPresentationMode(),
    });
    if (config?.scene_layout_settings?.productMode === 'assembly') {
        const editingAssembly = productMeta.editingCartItemCadOptions?.sceneAssembly;
        primeSceneConfiguratorAssemblyState(config, { assemblyOverride: editingAssembly || null });
    }
    const editingCartItemSelectedOptions = Array.isArray(productMeta?.editingCartItemSelectedOptions)
        ? productMeta.editingCartItemSelectedOptions
        : [];
    const productOptionsPlacementConfig = productMeta?.productOptionsPlacementConfig || {};
    const productOptionsDefaultOverrides = Array.isArray(productOptionsPlacementConfig.defaultOverrides)
        ? productOptionsPlacementConfig.defaultOverrides
        : (Array.isArray(productOptionsPlacementConfig.defaultSelectionOverrides)
            ? productOptionsPlacementConfig.defaultSelectionOverrides
            : (Array.isArray(productMeta?.configuratorOptions?.defaultOverrides)
                ? productMeta.configuratorOptions.defaultOverrides
                : []));
    const productOptionsHiddenOptions = Array.isArray(productOptionsPlacementConfig.hiddenOptions)
        ? productOptionsPlacementConfig.hiddenOptions
        : (Array.isArray(productMeta?.configuratorOptions?.hiddenOptions) ? productMeta.configuratorOptions.hiddenOptions : []);
    const productOptionsChoiceOverrides = Array.isArray(productOptionsPlacementConfig.choiceOverrides)
        ? productOptionsPlacementConfig.choiceOverrides
        : (Array.isArray(productMeta?.configuratorOptions?.choiceOverrides) ? productMeta.configuratorOptions.choiceOverrides : []);

    // Initialize ProductOptions first so we can capture the fully seeded
    // default config before CAD boots.
    if (config.product_options_enabled && !useExternalProductOptionsOnly && typeof ProductOptions !== 'undefined' && Array.isArray(productOptionsData)) {
        let initialProductOptionsConfig = {};
        productOptions = new ProductOptions();
        const hasOptions = productOptions.init((newConfig, rebuild = true) => {
            latestProductOptionsConfig = newConfig || {};
            if (!sceneLoader) {
                initialProductOptionsConfig = newConfig;
                return;
            }
            sceneBootstrapDebugLog('Product options changed. Rebuild:', rebuild);
            applyProductOptionsCadConfigToSceneLoader(sceneLoader, newConfig, rebuild, { rampDevOverlay });
        }, buildProductOptionsInitOptions(config, {
            options: productOptionsData,
            mobileOverrideSkin: resolveProductOptionsMobileOverrideSkin(config.product_options_mobile_override_skin || ''),
            currentProductId: productMeta?.id || 0,
            defaultSelectionOverrides: productOptionsDefaultOverrides,
            hiddenOptions: productOptionsHiddenOptions,
            choiceOverrides: productOptionsChoiceOverrides,
        }));
        
        if (hasOptions) {
            sceneBootstrapDebugLog('ProductOptions UI initialized with skin:', config.product_options_skin, 'collapsible:', config.product_options_collapsible, 'showPricing:', config.product_options_show_pricing, 'stickyPricing:', config.product_options_sticky_pricing, 'showPricingCollapsed:', config.product_options_show_pricing_collapsed);
        }

        window.sceneConfiguratorProductOptionsController = productOptions;

    if (editingCartItemSelectedOptions.length > 0) {
        productOptions.syncSelectionsFromSnapshot(editingCartItemSelectedOptions);
        initialProductOptionsConfig = productOptions.getConfig();
        latestProductOptionsConfig = initialProductOptionsConfig || {};
    }

        const initialCadOptions = sanitizeCadOptions(initialProductOptionsConfig);
        sceneBootstrapDebugLog('[newScene] Initial CAD options from product options:', initialCadOptions);
        config.cad_options = { ...config.cad_options, ...initialCadOptions };
    } else if (useExternalProductOptionsOnly) {
        window.sceneConfiguratorProductOptionsController = null;
        sceneBootstrapDebugLog('ProductOptions are externally controlled for this presentation scene');
    } else if (!config.product_options_enabled) {
        sceneBootstrapDebugLog('ProductOptions disabled for this scene');
    }

    const editingCartItemCadOptions = sanitizeCadOptions(productMeta?.editingCartItemCadOptions || {});
    const hasEditingCartItemCadOptions = Object.keys(editingCartItemCadOptions).length > 0;
    if (Object.keys(editingCartItemCadOptions).length > 0) {
        sceneBootstrapDebugLog('[newScene] Applying cart item CAD options from edit link:', editingCartItemCadOptions);
        config.cad_options = { ...config.cad_options, ...editingCartItemCadOptions };
    }

    const sharedCadOptions = {
        ...getLegacySharedCadOptionsFromUrl(),
        ...(await getSharedCadOptionsFromToken())
    };
    const hasSharedCadOptions = Object.keys(sharedCadOptions).length > 0;
    if (hasSharedCadOptions) {
        sceneBootstrapDebugLog('[newScene] Applying shared CAD options from URL:', sharedCadOptions);
        config.cad_options = { ...config.cad_options, ...sharedCadOptions };
    }

    if (productOptions && (hasEditingCartItemCadOptions || hasSharedCadOptions)) {
        productOptions.syncSelectionsFromConfig(config.cad_options || {});
        latestProductOptionsConfig = productOptions.getConfig?.() || latestProductOptionsConfig;
        productOptions.captureInitialState?.();
    }

    if (productOptions?.isCartUpdateMode?.()) {
        productOptions.captureCartSyncedSelectionState?.({ render: true });
    }

    applyProductContext(config);

    sceneLoader = new SceneLoader(canvas, config);
    sceneLoader.productOptions = productOptions;
    sceneLoader.setCadHandoffDebugSource?.(latestProductOptionsConfig);
    applyPresentationHostFrameSizing();
    
    // Initialize
    await sceneLoader.init();
    applyPresentationHostFrameSizing();
    sceneLoader.handleResize();
    bindExternalConfiguratorOptionsStateSync({
        productMeta,
        sceneLoader,
        rampDevOverlayRef: () => rampDevOverlay,
    });
    applyLatestExternalConfiguratorOptionsState({
        productMeta,
        sceneLoader,
        rampDevOverlayRef: () => rampDevOverlay,
    });
    window.requestAnimationFrame(() => {
        applyPresentationHostFrameSizing();
        sceneLoader.handleResize();
    });

    if (shouldShowSharedARPrompt) {
        window.setTimeout(() => {
            sceneLoader?.showSharedARLaunchPrompt?.();
        }, 300);
    }

    if (sceneLoader?.shouldAutoShowOnboarding?.()) {
        const onboardingSettings = sceneLoader?.getOnboardingSettings?.() || {};
        const walkthroughMode = onboardingSettings.mode === 'tap' ? 'tap' : 'sequence';
        window.setTimeout(() => {
            sceneLoader?.showOnboardingCallouts?.({ mode: walkthroughMode, markSeen: true });
        }, 700);
    }

    if (enableRamdevControls) {
        rampDevOverlay = mountRampDevOverlay({
            sceneLoader,
            initialCadOptions: config.cad_options || {},
            parameterMetadata: sceneLoader.cadModule?.parameterMetadata || [],
        });
    }
    window.sceneConfiguratorRampDevOverlay = rampDevOverlay;
    
    // Setup fullscreen button
    const fullscreenBtn = document.getElementById('fullscreen-btn');
    if (fullscreenBtn) {
        fullscreenBtn.addEventListener('click', () => {
            sceneLoader.toggleFullscreen();
        });
    }
    
    sceneBootstrapDebugLog("Scene initialized:", config.name);
    
    // Expose for debugging
    window.sceneLoader = sceneLoader;
    window.sceneConfiguratorResetOnboarding = () => {
        sceneLoader?.clearOnboardingSeen?.();
        const onboardingSettings = sceneLoader?.getOnboardingSettings?.() || {};
        sceneLoader?.showOnboardingCallouts?.({ mode: onboardingSettings.mode === 'tap' ? 'tap' : 'sequence' });
    };
}
